import { useCallback, useRef } from 'react';

/**
 * Long-press for touch, without breaking scrolling.
 *
 * The mobile apps render the web chat UI in a WebView, so this is how the
 * message action bar is reached on a phone — there is no separate native
 * gesture to maintain.
 *
 * Two details matter:
 *   - the timer is cancelled once the finger moves past a small threshold,
 *     otherwise every scroll through the conversation would fire an action bar
 *   - only touch/pen trigger it; a mouse uses hover instead, and firing both
 *     would make the bar appear on a slow desktop click
 */
export function useLongPress(onLongPress, { delay = 450, moveTolerance = 10 } = {}) {
  const timer = useRef(null);
  const origin = useRef({ x: 0, y: 0 });

  const clear = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const onPointerDown = useCallback(
    (event) => {
      if (event.pointerType === 'mouse') return;
      origin.current = { x: event.clientX, y: event.clientY };
      clear();
      timer.current = setTimeout(() => onLongPress(), delay);
    },
    [onLongPress, delay, clear]
  );

  const onPointerMove = useCallback(
    (event) => {
      if (!timer.current) return;
      const dx = Math.abs(event.clientX - origin.current.x);
      const dy = Math.abs(event.clientY - origin.current.y);
      if (dx > moveTolerance || dy > moveTolerance) clear();
    },
    [clear, moveTolerance]
  );

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: clear,
    onPointerCancel: clear,
    onPointerLeave: clear,
  };
}

export default useLongPress;
